import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { pscQueryKey } from '@/hooks/federal-contract/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import { PaginatedResponse } from '@/types/Api'
import type { PSC } from '@/types/federal-contract/Api'

interface UsePscCodesArgs {
  codes: string[]
}

export const fetchPscCodes = async (
  codes: Array<string>,
  accessToken: string,
): Promise<PaginatedResponse<PSC>> => {
  if (codes.length === 0) {
    return { count: 0, has_more: false, data: [] }
  }
  if (accessToken === null || accessToken === undefined) {
    throw new Error('No access token provided.')
  }
  const idString =
    codes
      .slice(0, -1)
      .map((code) => `psc_codes=${code}&`)
      .join('') + `psc_codes=${codes[codes.length - 1]}`
  const url = `${API_URL}/api/v1/psc/?${idString}`
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }
  return (await res.json()) as PaginatedResponse<PSC>
}

const selectPscCodes = (data: PaginatedResponse<PSC>) => data.data

export const usePscCodes = (args: UsePscCodesArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const query = useQuery({
    queryFn: () => fetchPscCodes(args.codes, accessToken),
    queryKey: pscQueryKey.codes(args),
    staleTime: Infinity,
    select: selectPscCodes,
  })
  return query
}
